import React, { useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InstructionTitle from '../Instructions/InstructionTitle';
import InstructionContent from '../Instructions/InstructionContent';
import { Button, Divider } from 'antd';
import * as actions from '../../store/actions';
import { fetchAssessment } from '../../store/actions/AssessmentActions';
import { updateQuestionDetails } from '../../Utils/questionDetailsUpdater';
import { DEFAULT_RESPONSE_DETAILS, SECTION_STATUS_IN_PROGRESS } from '../../const';
import { setProgress, setQuestionDetails } from '../../store/actions';
import styles from './SectionModal.scss';
import analytics from '../../services/analytics';

const responseDetails = {
  id: '',
  visited: false,
  answered: false,
  markedForReview: false,
  answer: [],
};

const SectionsModal = () => {
  const { t } = useTranslation('modal');

  const { section_id, assessment_id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const sections = useSelector(state => state.assessment.sections);
  const { mcqsResponses, progress } = useSelector(state => state.assessment);

  const section = useMemo(() => sections.find(s => s.id === section_id), [sections, section_id]);

  const location = useLocation();
  // Try to infer the currently displayed question (if any) from the URL so
  // we can include it when reporting the modal impression.
  const currentQuestionId = useMemo(() => {
    if (!location || !location.pathname) return undefined;
    const mcqMatch = location.pathname.match(/\/mcqs\/([^/]+)/);
    if (mcqMatch) return mcqMatch[1];
    const probMatch = location.pathname.match(/\/problem\/([^/]+)/);
    if (probMatch) return probMatch[1];
    return undefined;
  }, [location]);

  // Track modal impressions when the section becomes available
  useEffect(() => {
    if (!section) return;
    try {
      analytics.trackSectionModalShown(section.id, assessment_id, currentQuestionId);
    } catch (e) {
      // best-effort analytics
    }
  }, [section, assessment_id, currentQuestionId]);

  useEffect(() => {
    const handlePopstate = e => {
      window.history.go(1);
    };
    if (!window.popstateListenerAdded) {
      window.addEventListener('popstate', handlePopstate);
      window.popstateListenerAdded = true;
    }
    if (!sessionStorage.getItem('assessment_id')) {
      sessionStorage.setItem('assessment_id', assessment_id);
    }
  }, []);

  useEffect(() => {
    if (sections.length === 0 || (section && sections[0].id === section.id)) {
      dispatch(fetchAssessment(assessment_id));
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      actions.updateSectionStatusLocal({
        sectionId: section_id,
        status: SECTION_STATUS_IN_PROGRESS,
      }),
    );
  }, [section_id, dispatch]);

  if (!section) {
    return <div></div>;
  }

  const goToSectionOnClick = () => {
    dispatch(actions.setCurrentSectionId(section.id));

    try {
      analytics.trackGoToSection(section.id, assessment_id, 'sections_modal');
    } catch (e) {
      // best-effort analytics, don't block navigation
    }

    if (section.type === 'mcqs') {
      const currentMcqIds = section.mcqs.map(mcqId => ({ id: mcqId }));
      const newQuestionDetails = updateQuestionDetails(
        currentMcqIds,
        mcqsResponses,
        DEFAULT_RESPONSE_DETAILS,
      );

      dispatch(setQuestionDetails(newQuestionDetails));
      dispatch(setProgress({ ...progress, total: currentMcqIds.length }));
      dispatch(actions.getRecoredResponses(assessment_id));

      history.push(`/assessment/${assessment_id}/mcqs/${section.mcqs[0]}`);
    } else {
      history.push(`/assessment/${assessment_id}/problem/${section.problems[0]}`);
    }
  };

  const totalQuestion = {
    mcqs: section.mcqs?.length || 0,
    problems: section.problems?.length || 0,
  };
  const sectionTimer = section.total_time;

  return (
    <div className={styles['sectionModal-wrapper']}>
      <div className={styles['sectionModal-container']}>
        <InstructionTitle
          totalQuestion={totalQuestion}
          timer={sectionTimer}
          title={section.title}
        />
        <InstructionContent content={section.instructions} />
        <Divider className={styles['sectionModal-divider']} />
        <Button
          className={styles['section-btn']}
          type="primary"
          onClick={goToSectionOnClick}
        >
          {t('section_instruction.go_to_section')}
        </Button>
      </div>
    </div>
  );
};

export default SectionsModal;
